import { useRef } from 'react';
import { FieldArray } from 'formik';
import { useTheme } from 'styled-components';
import { v4 as uuid } from 'uuid';

import { UseFormik } from '@/utils/Formik/types';

import { FileProps } from '@/context/FileContext/types';
import { useToast } from '@/context/ToastContext';

import { useMobile } from '@/hooks/useMobile/useMobile';

import {
  classifyItemType,
  getItemOptionsByProblemType,
} from '@/pages/DetailsDamage/components/CreateDamage/constants';

import SimpleSelect from '@/components/SimpleSelect';
import TextField from '@/components/TextField';
import { ErrorIcon } from '@/components/TextField/styles';

import {
  AddButtonContainer,
  DamageTable,
  DamageTableContainer,
  DamageTd,
  DamageTh,
  DamageThead,
  IconButton,
  StepperBtn,
  StepperContainer,
} from '../../styles';

import { PaperClipIcon, PlusIcon, TrashIcon, UploadIcon } from './icons';
import { BlueTextFieldWrapper } from './styles';

type ItemsTableProps = {
  category: string;
  formik: UseFormik<any>;
};

const formatToBRL = (value: string): string => {
  let cleanedValue = value.replace(/\D/g, '');

  if (cleanedValue.length === 0) {
    return '';
  }

  while (cleanedValue.length < 3) {
    cleanedValue = `0${cleanedValue}`;
  }

  const cents = cleanedValue.slice(-2);
  let reals = cleanedValue.slice(0, -2);

  reals = reals.replace(/^0+/, '');
  if (reals.length === 0) {
    reals = '0';
  }

  // 5. Adiciona separador de milhar (ponto)
  reals = reals.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  return `R$ ${reals},${cents}`;
};

export const ManualError = ({ message }: { message?: string }) => {
  if (!message) return null;
  return (
    <div
      style={{
        marginTop: '4px',
        display: 'flex',
        alignItems: 'center',
        fontSize: '12px',
        gap: '2px',
        color: '#EB6957',
        fontWeight: 500,
      }}
    >
      <ErrorIcon />
      {message}
    </div>
  );
};

const ItemsTable = ({ category, formik }: ItemsTableProps) => {
  const { values, setFieldValue, handleChange, touched, errors } = formik;
  const fileInputRefs = useRef<{ [key: number]: HTMLInputElement | null }>({});
  const { isMobile } = useMobile();
  const toast = useToast();
  const theme = useTheme();

  const handleUploadClick = (index: number) => {
    fileInputRefs.current[index]?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const { files } = e.target;
    if (!files || files.length === 0) {
      e.target.value = '';
      return;
    }

    const currentEvidenceFiles = values.items[index].evidenceFiles || [];
    const remainingSlots = 5 - currentEvidenceFiles.length;

    if (remainingSlots <= 0) {
      toast.error('Você já atingiu o limite de 5 evidências por item');
      e.target.value = '';
      return;
    }

    // Validar tipos de arquivo permitidos
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf', 'video/mp4', 'video/quicktime', 'video/x-ms-wmv', 'video/webm', 'video/x-flv'];
    const filesToAdd = Array.from(files).slice(0, remainingSlots);
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    for (const file of filesToAdd) {
      if (allowedTypes.includes(file.type)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    }

    // Mostrar erro se houver arquivos inválidos
    if (invalidFiles.length > 0) {
      toast.error(`Apenas JPG, PNG, PDF e vídeos (MP4, MOV, WMV, WebM, FLV) são aceitos.`);
    }

    // Se não houver arquivos válidos, limpar input e retornar
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    const newFileProps: FileProps[] = [];

    for (const file of validFiles) {
      const fileProps: FileProps = {
        id: uuid(),
        uid: undefined,
        file,
        name: file.name,
        size: file.size,
        readableSize: `${(file.size / 1024).toFixed(2)} KB`,
        MIMEtype: file.type,
        previewURL: URL.createObjectURL(file),
        url: null,
        uploadProgress: 0,
        uploaded: false,
        error: false,
        content_type: file.type,
        originalFile: file,
        type: file.type,
      };
      newFileProps.push(fileProps);
    }

    setFieldValue(`items.${index}.evidenceFiles`, [...currentEvidenceFiles, ...newFileProps]);

    setTimeout(() => {
      formik.setFieldTouched(`items.${index}.evidenceFiles`, true);
      formik.validateField(`items.${index}.evidenceFiles`);
    }, 0);

    e.target.value = '';
  };

  const handleRemoveEvidenceFile = (itemIndex: number, evidenceIndex: number) => {
    const currentEvidenceFiles = values.items[itemIndex].evidenceFiles || [];
    const updatedFiles = currentEvidenceFiles.filter(
      (_: any, idx: number) => idx !== evidenceIndex,
    );
    setFieldValue(`items.${itemIndex}.evidenceFiles`, updatedFiles);
  };

  const handlePriceChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    index: number,
  ) => {
    const rawValue = e.target.value;

    const formattedValue = formatToBRL(rawValue);

    setFieldValue(`items.${index}.itemPrice`, formattedValue);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
    ];

    if (allowedKeys.includes(e.key) || /^[0-9]$/.test(e.key)) {
      return;
    }

    e.preventDefault();
  };

  const getItemErrors = (index: number) => {
    const itemErrors = Array.isArray(errors?.items)
      ? (errors?.items[index] as Record<string, unknown> | undefined)
      : undefined;

    const itemTouched = Array.isArray(touched?.items)
      ? (touched?.items[index] as Record<string, boolean> | undefined)
      : undefined;

    return {
      description:
        itemTouched?.description && itemErrors?.description
          ? (itemErrors.description as string)
          : undefined,
      itemName:
        itemTouched?.itemName && itemErrors?.itemName ? (itemErrors.itemName as string) : undefined,
      itemPrice:
        itemTouched?.itemPrice && itemErrors?.itemPrice
          ? (itemErrors.itemPrice as string)
          : undefined,
      evidenceFiles:
        itemTouched?.evidenceFiles && itemErrors?.evidenceFiles
          ? (itemErrors.evidenceFiles as string)
          : undefined,
    };
  };

  const itemNameOptions = getItemOptionsByProblemType(category).map((item) => {
    return {
      value: item.value,
      valueLabel: item.label,
    };
  });

  if (isMobile) {
    return (
      <div>
        <FieldArray
          name="items"
          render={(arrayHelpers) => (
            <>
              {values.items && values.items.length > 0 ? (
                values.items.map((item: any, index: number) => {
                  const itemErrors = getItemErrors(index);
                  const isLastItem = index === values.items.length - 1;

                  return (
                    <div key={index}>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '24px',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '16px',
                              alignItems: 'center',
                            }}
                          >
                            <SimpleSelect
                              id={`items.${index}.itemName`}
                              label="Item"
                              options={itemNameOptions}
                              onChange={(e) => {
                                const selectedName = e.target.value;
                                setFieldValue(`items.${index}.itemName`, selectedName);
                                const autoType = classifyItemType(selectedName);

                                if (selectedName === 'other') {
                                  setFieldValue(`items.${index}.itemType`, 'Outro');
                                } else {
                                  setFieldValue(`items.${index}.itemType`, autoType);
                                }

                                handleChange(e);

                                // Validar após mudança
                                setTimeout(() => {
                                  formik.setFieldTouched(`items.${index}.itemName`, true);
                                  formik.validateField(`items.${index}.itemName`);
                                }, 0);
                              }}
                              placeholder={
                                !category ? 'Selecione a categoria primeiro' : 'Selecione'
                              }
                              labelClassName="label"
                              error={!!itemErrors.itemName}
                              formik={formik}
                              disabled={!category}
                            />

                            <IconButton
                              onClick={() => arrayHelpers.remove(index)}
                              style={{ marginTop: '24px' }}
                            >
                              <TrashIcon />
                            </IconButton>
                          </div>

                          <ManualError message={itemErrors.itemName} />
                          {!category && <ManualError message="Selecione a categoria primeiro" />}
                        </div>

                        <div>
                          <TextField
                            id={`items.${index}.description`}
                            placeholder="Descreva o ocorrido..."
                            label="Ocorrido *"
                            labelClassName="label"
                            value={item.description}
                            onChange={handleChange}
                            onBlur={() => {
                              formik.setFieldTouched(`items.${index}.description`, true);
                              formik.validateField(`items.${index}.description`);
                            }}
                            error={!!itemErrors.description}
                            formik={formik}
                          />
                          <ManualError message={itemErrors.description} />
                        </div>

                        <div>
                          <TextField
                            id={`items.${index}.itemPrice`}
                            placeholder="R$ 0,00"
                            label="Valor"
                            labelClassName="label"
                            value={item.itemPrice}
                            onChange={(e) => handlePriceChange(e, index)}
                            onKeyPress={handleKeyPress}
                            onBlur={() => {
                              formik.setFieldTouched(`items.${index}.itemPrice`, true);
                              formik.validateField(`items.${index}.itemPrice`);
                            }}
                            error={!!itemErrors.itemPrice}
                            formik={formik}
                            type="tel"
                            pattern="[0-9]*"
                          />

                          <ManualError message={itemErrors.itemPrice} />
                        </div>

                        <div>
                          {/* Exibir evidências existentes */}
                          {item.evidenceFiles && item.evidenceFiles.length > 0 && (
                            <div style={{ marginBottom: '8px', maxWidth: '100%' }}>
                              {item.evidenceFiles.map((file: FileProps, evidenceIdx: number) => (
                                <p
                                  key={`file-${evidenceIdx}`}
                                  style={{
                                    fontSize: '12px',
                                    color: '#0066CC',
                                    marginBottom: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    backgroundColor: '#eff6ff',
                                    padding: '8px 8px',
                                    borderRadius: '8px',
                                  }}
                                >
                                  <div
                                    className="evidence-file"
                                    style={{
                                      flex: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                    }}
                                  >
                                    <PaperClipIcon />
                                    <span
                                      style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={file.name}
                                    >
                                      {file.name.length > 50
                                        ? `${file.name.substring(0, 50)}...`
                                        : file.name}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    tabIndex={0}
                                    onClick={() => handleRemoveEvidenceFile(index, evidenceIdx)}
                                    onFocus={(e) => {
                                      e.currentTarget.style.outline = '2px solid #3b82f6';
                                      e.currentTarget.style.outlineOffset = '2px';
                                    }}
                                    onBlur={(e) => {
                                      e.currentTarget.style.outline = 'none';
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#EB6957',
                                      cursor: 'pointer',
                                      padding: '2px',
                                      fontSize: '16px',
                                    }}
                                    title="Remover evidência"
                                  >
                                    ×
                                  </button>
                                </p>
                              ))}
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div
                              tabIndex={0}
                              role="button"
                              style={{
                                flex: 1,
                                cursor: 'pointer',
                                borderRadius: '4px',
                                outline: 'none',
                              }}
                              onClick={() => handleUploadClick(index)}
                              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleUploadClick(index);
                                }
                              }}
                            >
                              <BlueTextFieldWrapper
                                hasFile={false}
                                style={{ pointerEvents: 'none' }}
                              >
                                <TextField
                                  id={`items.${index}.evidencePlaceholder`}
                                  placeholder="Faça o upload de foto/vídeo/arquivo"
                                  label="Evidências"
                                  labelClassName="label"
                                  value={
                                    item.evidenceFiles && item.evidenceFiles.length > 0
                                      ? `${item.evidenceFiles.length} arquivo${item.evidenceFiles.length > 1 ? 's' : ''} selecionado${item.evidenceFiles.length > 1 ? 's' : ''}`
                                      : ''
                                  }
                                  disabled
                                  error={!!itemErrors.evidenceFiles}
                                  formik={formik}
                                  className="evidence-placeholder"
                                />
                              </BlueTextFieldWrapper>
                            </div>

                            <IconButton
                              tabIndex={0}
                              onClick={() => handleUploadClick(index)}
                              onFocus={(e) => {
                                e.currentTarget.style.outline = '2px solid #3b82f6';
                                e.currentTarget.style.outlineOffset = '2px';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.outline = 'none';
                              }}
                              title="Upload de evidências"
                              style={{
                                marginTop: '24px',
                              }}
                            >
                              <UploadIcon />
                            </IconButton>

                            <input
                              type="file"
                              style={{ display: 'none' }}
                              ref={(el) => (fileInputRefs.current[index] = el)}
                              accept="image/jpeg,image/jpg,image/png,application/pdf,video/mp4,video/quicktime,video/x-ms-wmv,video/webm,video/x-flv"
                              multiple
                              onChange={(e) => handleFileChange(e, index)}
                            />
                          </div>

                          <ManualError message={itemErrors.evidenceFiles} />
                        </div>

                        <StepperContainer>
                          <StepperBtn
                            type="button"
                            onClick={() => {
                              const currentValue = item.quantity || 1;
                              const newValue = Math.max(1, currentValue - 1);

                              setFieldValue(`items.${index}.quantity`, newValue);
                            }}
                            disabled={item.quantity <= 1}
                            style={{
                              opacity: item.quantity <= 1 ? 0.3 : 1,
                              cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            -
                          </StepperBtn>

                          <span style={{ fontSize: '14px', minWidth: '20px', textAlign: 'center' }}>
                            {item.quantity}
                          </span>

                          <StepperBtn
                            type="button"
                            onClick={() => {
                              const currentValue = item.quantity || 0;
                              setFieldValue(`items.${index}.quantity`, currentValue + 1);
                            }}
                          >
                            +
                          </StepperBtn>
                        </StepperContainer>
                      </div>

                      {!isLastItem && (
                        <div
                          style={{
                            height: '1px',
                            width: '100%',
                            background: '#C6C6C6',
                            margin: '24px 0px',
                          }}
                        />
                      )}

                      {isLastItem && (
                        <AddButtonContainer
                          style={{
                            marginTop: '16px',
                            width: '100%',
                          }}
                          type="button"
                          onClick={() =>
                            arrayHelpers.push({
                              id: uuid(),
                              description: '',
                              itemName: '',
                              itemPrice: '',
                              quantity: 1,
                              itemType: undefined,
                              other: '',
                              evidenceFiles: [],
                            })
                          }
                        >
                          <PlusIcon />
                          Adicionar Item
                        </AddButtonContainer>
                      )}
                    </div>
                  );
                })
              ) : (
                <div>
                  <span style={{ textAlign: 'center', color: '#888', padding: '16px 0px' }}>
                    Nenhum item adicionado.
                  </span>

                  <AddButtonContainer
                    style={{
                      marginTop: '16px',
                      width: '100%',
                    }}
                    type="button"
                    onClick={() =>
                      arrayHelpers.push({
                        id: uuid(),
                        itemName: '',
                        itemPrice: '',
                        quantity: 1,
                        itemType: undefined,
                        other: '',
                        evidenceFiles: [],
                      })
                    }
                  >
                    <PlusIcon />
                    Adicionar Item
                  </AddButtonContainer>
                </div>
              )}
            </>
          )}
        />
      </div>
    );
  }

  return (
    <DamageTableContainer>
      <DamageTable>
        <DamageThead>
          <tr>
            <DamageTh style={{ width: '20%' }}>Item</DamageTh>
            <DamageTh style={{ width: '20%' }}>Ocorrido</DamageTh>
            <DamageTh style={{ width: '15%' }}>Valor</DamageTh>
            <DamageTh style={{ width: '25%' }}>Evidências</DamageTh>
            <DamageTh style={{ width: '10%' }}>Qtd</DamageTh>
            <DamageTh style={{ width: '10%', textAlign: 'center' }}>Ações</DamageTh>
          </tr>
        </DamageThead>

        <FieldArray
          name="items"
          render={(arrayHelpers) => (
            <tbody>
              {values.items && values.items.length > 0 ? (
                values.items.map((item: any, index: number) => {
                  const itemErrors = getItemErrors(index);

                  return (
                    <tr key={index}>
                      <DamageTd>
                        <SimpleSelect
                          id={`items.${index}.itemName`}
                          options={itemNameOptions}
                          onChange={(e) => {
                            const selectedName = e.target.value;

                            setFieldValue(`items.${index}.itemName`, selectedName);

                            const autoType = classifyItemType(selectedName);

                            if (selectedName === 'other') {
                              setFieldValue(`items.${index}.itemType`, 'Outro');
                            } else {
                              setFieldValue(`items.${index}.itemType`, autoType);
                            }

                            handleChange(e);

                            setTimeout(() => {
                              formik.setFieldTouched(`items.${index}.itemName`, true);
                              formik.validateField(`items.${index}.itemName`);
                            }, 0);
                          }}
                          placeholder={!category ? 'Selecione a categoria primeiro' : 'Selecione'}
                          error={!!itemErrors.itemName}
                          formik={formik}
                          disabled={!category}
                        />

                        <ManualError message={itemErrors.itemName} />
                        {!category && <ManualError message="Selecione a categoria primeiro" />}
                      </DamageTd>

                      <DamageTd>
                        <TextField
                          id={`items.${index}.description`}
                          placeholder="Descreva o ocorrido..."
                          value={item.description}
                          onChange={handleChange}
                          onBlur={() => {
                            formik.setFieldTouched(`items.${index}.description`, true);
                            formik.validateField(`items.${index}.description`);
                          }}
                          error={!!itemErrors.description}
                          formik={formik}
                        />
                        <ManualError message={itemErrors.description} />
                      </DamageTd>

                      <DamageTd>
                        <TextField
                          id={`items.${index}.itemPrice`}
                          placeholder="R$ 0,00"
                          value={item.itemPrice}
                          onChange={(e) => handlePriceChange(e, index)}
                          onKeyPress={handleKeyPress}
                          onBlur={() => {
                            formik.setFieldTouched(`items.${index}.itemPrice`, true);
                            formik.validateField(`items.${index}.itemPrice`);
                          }}
                          error={!!itemErrors.itemPrice}
                          formik={formik}
                          type="tel"
                          pattern="[0-9]*"
                        />

                        <ManualError message={itemErrors.itemPrice} />
                      </DamageTd>

                      <DamageTd>
                        <div>
                          {/* Exibir evidências existentes */}
                          {item.evidenceFiles && item.evidenceFiles.length > 0 && (
                            <div style={{ marginBottom: '8px' }}>
                              {item.evidenceFiles.map((file: FileProps, evidenceIdx: number) => (
                                <p
                                  key={`file-${evidenceIdx}`}
                                  style={{
                                    fontSize: '12px',
                                    color: '#0066CC',
                                    marginBottom: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    backgroundColor: '#eff6ff',
                                    padding: '8px 8px',
                                    borderRadius: '8px',
                                  }}
                                >
                                  <div
                                    className="evidence-file"
                                    style={{
                                      flex: 1,
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                    }}
                                  >
                                    <PaperClipIcon />
                                    <span
                                      style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={file.name}
                                    >
                                      {file.name.length > 50
                                        ? `${file.name.substring(0, 50)}...`
                                        : file.name}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    tabIndex={0}
                                    onClick={() => handleRemoveEvidenceFile(index, evidenceIdx)}
                                    onFocus={(e) => {
                                      e.currentTarget.style.outline = '2px solid #3b82f6';
                                      e.currentTarget.style.outlineOffset = '2px';
                                    }}
                                    onBlur={(e) => {
                                      e.currentTarget.style.outline = 'none';
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#EB6957',
                                      cursor: 'pointer',
                                      padding: '2px',
                                      fontSize: '16px',
                                    }}
                                    title="Remover evidência"
                                  >
                                    ×
                                  </button>
                                </p>
                              ))}
                            </div>
                          )}

                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <div
                              tabIndex={0}
                              role="button"
                              style={{
                                flex: 1,
                                cursor: 'pointer',
                                borderRadius: '4px',
                                outline: 'none',
                              }}
                              onClick={() => handleUploadClick(index)}
                              onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleUploadClick(index);
                                }
                              }}
                            >
                              <BlueTextFieldWrapper
                                hasFile={false}
                                style={{ pointerEvents: 'none' }}
                              >
                                <TextField
                                  id={`items.${index}.evidencePlaceholder`}
                                  placeholder="Faça o upload de foto/vídeo/arquivo"
                                  value={
                                    item.evidenceFiles && item.evidenceFiles.length > 0
                                      ? `${item.evidenceFiles.length} arquivo${item.evidenceFiles.length > 1 ? 's' : ''} selecionado${item.evidenceFiles.length > 1 ? 's' : ''}`
                                      : ''
                                  }
                                  disabled
                                  error={!!itemErrors.evidenceFiles}
                                  formik={formik}
                                />
                              </BlueTextFieldWrapper>
                            </div>

                            <IconButton
                              tabIndex={0}
                              onClick={() => handleUploadClick(index)}
                              onFocus={(e) => {
                                e.currentTarget.style.outline = '2px solid #3b82f6';
                                e.currentTarget.style.outlineOffset = '2px';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.outline = 'none';
                              }}
                              title="Upload de evidências"
                              style={{
                                marginTop: '8px',
                              }}
                            >
                              <UploadIcon />
                            </IconButton>

                            <input
                              type="file"
                              style={{ display: 'none' }}
                              ref={(el) => (fileInputRefs.current[index] = el)}
                              accept="image/jpeg,image/jpg,image/png,application/pdf,video/mp4,video/quicktime,video/x-ms-wmv,video/webm,video/x-flv"
                              multiple
                              onChange={(e) => handleFileChange(e, index)}
                            />
                          </div>

                          <ManualError message={itemErrors.evidenceFiles} />
                        </div>
                      </DamageTd>

                      <DamageTd>
                        <StepperContainer>
                          <StepperBtn
                            type="button"
                            onClick={() => {
                              const currentValue = item.quantity || 1;
                              const newValue = Math.max(1, currentValue - 1);

                              setFieldValue(`items.${index}.quantity`, newValue);
                            }}
                            disabled={item.quantity <= 1}
                            style={{
                              opacity: item.quantity <= 1 ? 0.3 : 1,
                              cursor: item.quantity <= 1 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            -
                          </StepperBtn>

                          <span style={{ fontSize: '14px', minWidth: '20px', textAlign: 'center' }}>
                            {item.quantity}
                          </span>

                          <StepperBtn
                            type="button"
                            onClick={() => {
                              const currentValue = item.quantity || 0;
                              setFieldValue(`items.${index}.quantity`, currentValue + 1);
                            }}
                          >
                            +
                          </StepperBtn>
                        </StepperContainer>
                      </DamageTd>

                      <DamageTd>
                        <IconButton onClick={() => arrayHelpers.remove(index)}>
                          <TrashIcon />
                        </IconButton>
                      </DamageTd>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <DamageTd colSpan={6} style={{ textAlign: 'center', color: '#888' }}>
                    Nenhum item adicionado.
                  </DamageTd>
                </tr>
              )}
            </tbody>
          )}
        />
      </DamageTable>
    </DamageTableContainer>
  );
};

export default ItemsTable;
